/**
 * ============================================================================
 * DOCUMENT STORE — real local persistence for uploaded files
 * ============================================================================
 *
 * Uploaded documents are held in IndexedDB, as the actual `Blob` plus its
 * metadata, so a file survives a reload and can be re-used by any agent without
 * the user finding it on disk again. That is what separates this from the 2.1
 * bench, where the upload dialog recorded a file *name* and dropped the bytes.
 *
 * Why IndexedDB and not the session store: a Zustand store is memory, and the
 * whole point of "auto-save locally so I can re-use it" is that it outlives the
 * tab. Why not localStorage: it is a synchronous string store — a 40 MB
 * PowerPoint is neither a string nor something to block the main thread on.
 *
 * NOTE ON THE ADAPTER SPLIT: this is client-side storage, so it is shared by
 * both the static and the HTTP adapter rather than being reimplemented per
 * adapter. When a real document endpoint exists, the HTTP adapter can POST on
 * write and treat this as a local cache; nothing above the port changes.
 *
 * Availability is never assumed. In a private window, an old browser, or the
 * jsdom test environment `indexedDB` may be missing or refuse to open. Every
 * method degrades to a reported "unavailable" rather than throwing into a
 * render, because a bench that silently swallows uploads is worse than one that
 * says it cannot store them.
 */

import type { Id, StoredDocument, StoredDocumentMeta } from '../contracts';

const DB_NAME = 'pnet-arena-documents';
const DB_VERSION = 1;
const STORE = 'documents';
const IDX_UPLOADED = 'by-uploadedAt';

/** Shape as persisted. The blob rides alongside the metadata in one record. */
interface DocumentRecord extends StoredDocumentMeta {
  readonly blob: Blob;
}

/**
 * Raised when the browser will not give us a database. Callers catch this and
 * surface it; they must not treat it as "no documents".
 */
export class DocumentStoreUnavailableError extends Error {
  constructor(cause: string) {
    super(`Local document storage is unavailable: ${cause}`);
    this.name = 'DocumentStoreUnavailableError';
  }
}

function indexedDbOrNull(): IDBFactory | null {
  // `globalThis.indexedDB` is absent in node and can throw on access in some
  // sandboxed iframes, so the read itself is guarded.
  try {
    return typeof globalThis.indexedDB === 'undefined' ? null : globalThis.indexedDB;
  } catch {
    return null;
  }
}

/** True when this environment can persist documents at all. */
export function isDocumentStoreAvailable(): boolean {
  return indexedDbOrNull() !== null;
}

/** Wrap an IDBRequest as a promise; every call below goes through this. */
function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('IndexedDB request failed.'));
    };
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  const idb = indexedDbOrNull();
  if (idb === null) {
    return Promise.reject(new DocumentStoreUnavailableError('this browser exposes no indexedDB'));
  }
  // Cached, so concurrent uploads share one connection rather than racing to
  // run the upgrade transaction.
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const open = idb.open(DB_NAME, DB_VERSION);

    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        // Listing is always newest-first, so the sort is an index read.
        store.createIndex(IDX_UPLOADED, 'uploadedAt', { unique: false });
      }
    };
    open.onsuccess = () => {
      resolve(open.result);
    };
    open.onerror = () => {
      reject(
        new DocumentStoreUnavailableError(open.error?.message ?? 'the database would not open'),
      );
    };
    open.onblocked = () => {
      reject(new DocumentStoreUnavailableError('another tab is holding an older database version'));
    };
  }).catch((error: unknown) => {
    // A failed open must not poison every later attempt — the user may have
    // since closed the blocking tab.
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

async function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(STORE, mode);
  const store = transaction.objectStore(STORE);
  const result = await run(store);
  // Resolve only once the transaction commits, so a caller that reads straight
  // after a write cannot observe the pre-write state.
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onabort = () => {
      reject(transaction.error ?? new Error('The document transaction aborted.'));
    };
    transaction.onerror = () => {
      reject(transaction.error ?? new Error('The document transaction failed.'));
    };
  });
  return result;
}

/** Strip the blob — callers listing documents must not hold every file in memory. */
function toMeta(record: DocumentRecord): StoredDocumentMeta {
  const { blob: _blob, ...meta } = record;
  return meta;
}

/**
 * Every stored document, newest first.
 *
 * Returns `[]` only when the store is genuinely empty. When storage is
 * unavailable this rejects, so the caller can say so rather than render an
 * empty bench that looks like a deletion.
 */
export async function listDocuments(): Promise<readonly StoredDocumentMeta[]> {
  const records = await tx('readonly', (store) =>
    request(store.getAll() as IDBRequest<DocumentRecord[]>),
  );
  return records
    .map(toMeta)
    .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : a.uploadedAt > b.uploadedAt ? -1 : 0));
}

/** One document including its bytes. `undefined` when the id is unknown. */
export async function getDocument(id: Id): Promise<StoredDocument | undefined> {
  const record = await tx('readonly', (store) =>
    request(store.get(id) as IDBRequest<DocumentRecord | undefined>),
  );
  return record;
}

/** Insert or replace. Returns the metadata, so the caller need not keep the blob. */
export async function putDocument(document: StoredDocument): Promise<StoredDocumentMeta> {
  await tx('readwrite', (store) => request(store.put(document)));
  return toMeta(document);
}

export async function deleteDocument(id: Id): Promise<void> {
  await tx('readwrite', (store) => request(store.delete(id)));
}

/** Total bytes held locally — shown on the bench so the user can manage it. */
export async function usedBytes(): Promise<number> {
  const records = await tx('readonly', (store) =>
    request(store.getAll() as IDBRequest<DocumentRecord[]>),
  );
  return records.reduce((sum, record) => sum + record.size, 0);
}

/** Test seam: drops the cached connection so a fake `indexedDB` takes effect. */
export function resetDocumentStoreConnection(): void {
  dbPromise = null;
}
